/**
 * Texto dos times para colar no grupo do WhatsApp.
 *
 * Só nome e quem vai ao gol: nota e média são dado de administração e não
 * podem vazar num grupo onde todo mundo lê.
 */

export interface ShareableTeam {
  name: string;
  color: string;
  players: Array<{ name: string; isGoalkeeper: boolean; isGuest: boolean }>;
}

/** Cores dos presets de time (TEAM_PRESETS) → bolinha equivalente. */
const COLOR_EMOJI: Record<string, string> = {
  '#12B76A': '🟢',
  '#2E90FA': '🔵',
  '#F79009': '🟠',
  '#F04438': '🔴',
  '#9E77ED': '🟣',
  '#EAAA08': '🟡',
};

export function teamsToWhatsappText(input: {
  groupName: string;
  scheduledAt: string;
  venueName: string | null;
  teams: ShareableTeam[];
}): string {
  const date = new Date(input.scheduledAt);
  const day = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
  const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    date,
  );

  const header = [
    `⚽ *${input.groupName}*`,
    `📅 ${day} · ${time}`,
    input.venueName ? `📍 ${input.venueName}` : null,
  ].filter(Boolean);

  const teams = input.teams.map((team) => {
    const lines = [...team.players]
      .sort((a, b) => Number(b.isGoalkeeper) - Number(a.isGoalkeeper))
      .map(
        (player) =>
          `${player.isGoalkeeper ? '🧤' : '•'} ${player.name}${player.isGuest ? ' (convidado)' : ''}`,
      );

    const dot = COLOR_EMOJI[team.color.toUpperCase()] ?? '⚪';
    return [`${dot} *${team.name}*`, ...lines].join('\n');
  });

  return [header.join('\n'), ...teams].join('\n\n');
}

/**
 * Copia para a área de transferência. O fallback com textarea cobre navegador
 * sem a API assíncrona (ou sem permissão para ela).
 */
export async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // segue para o fallback
  }

  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);

  if (!ok) throw new Error('Não foi possível copiar');
}

/** Abre o WhatsApp com o texto pronto; a pessoa só escolhe o grupo. */
export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}
